import { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Form,
  Label,
  Input,
  FormFeedback,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Spinner,
} from "reactstrap";
import classnames from "classnames";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { useFormik } from "formik";
import * as Yup from "yup";

import {
  useGetAdminConfiguration,
  useUpdateAdminConfiguration,
} from "../../api/price";

const MONEY_FIELDS = [
  "weekendUplift",
  "nightUplift",
  "urgentUplift",
  "bankHolidayUplift",
  "holidayPay",
  "ni",
  "levy",
  "pension",
];

const toStringPrices = (values) =>
  MONEY_FIELDS.reduce((acc, key) => {
    acc[key] = values[key] != null ? String(values[key]) : "";
    return acc;
  }, {});

const PRICE_FIELDS = [
  { name: "weekendUplift", label: "Weekend uplift (%)" },
  { name: "nightUplift", label: "Night uplift (%)" },
  { name: "urgentUplift", label: "Urgent uplift (%)" },
  { name: "bankHolidayUplift", label: "Bank holiday uplift (%)" },
  { name: "holidayPay", label: "Holiday pay (%)" },
  { name: "levy", label: "Levy (%)" },
  { name: "pension", label: "Pension (%)" },
  { name: "ni", label: "NI (%)" },
  { name: "sspStartDay", label: "SSP start day", integer: true },
];

const moneyRegex = /^\d+(\.\d{1,2})?$/;

const PriceSchema = Yup.object({
  weekendUplift: Yup.string()
    .matches(moneyRegex, "Use up to 2 decimals")
    .required("Required"),
  nightUplift: Yup.string()
    .matches(moneyRegex, "Use up to 2 decimals")
    .required("Required"),
  urgentUplift: Yup.string()
    .matches(moneyRegex, "Use up to 2 decimals")
    .required("Required"),
  bankHolidayUplift: Yup.string()
    .matches(moneyRegex, "Use up to 2 decimals")
    .required("Required"),
  holidayPay: Yup.string()
    .matches(moneyRegex, "Use up to 2 decimals")
    .required("Required"),
  ni: Yup.string()
    .matches(moneyRegex, "Use up to 2 decimals")
    .required("Required"),
  levy: Yup.string()
    .matches(moneyRegex, "Use up to 2 decimals")
    .required("Required"),
  pension: Yup.string()
    .matches(moneyRegex, "Use up to 2 decimals")
    .required("Required"),
  sspStartDay: Yup.number()
    .transform((value, orig) =>
      orig === "" || orig === undefined ? undefined : Number(orig)
    )
    .typeError("Must be a number")
    .integer("Must be a whole number")
    .min(1)
    .max(31)
    .required("Required"),
});

const emptyRecord = {
  id: "",
  weekendUplift: "",
  nightUplift: "",
  urgentUplift: "",
  bankHolidayUplift: "",
  holidayPay: "",
  levy: "",
  pension: "",
  ni: "",
  sspStartDay: "",
  createdAt: "",
  updatedAt: "",
};

function extractConfiguration(mutationResult) {
  const raw = mutationResult?.data?.data ?? mutationResult?.data ?? {};
  if (raw && typeof raw === "object" && !Array.isArray(raw) && raw.id)
    return raw;
  return emptyRecord;
}

function SectionHeader({ title, meta }) {
  return (
    <div className="d-flex justify-content-between align-items-center mb-3">
      <h5 className="mb-0">{title}</h5>
      {meta && <small className="text-muted">{meta}</small>}
    </div>
  );
}

function PriceForm({
  title,
  loading,
  error,
  record,
  onRefresh,
  onSubmit,
  submitting,
}) {
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      weekendUplift: record.weekendUplift ?? "",
      nightUplift: record.nightUplift ?? "",
      urgentUplift: record.urgentUplift ?? "",
      bankHolidayUplift: record.bankHolidayUplift ?? "",
      holidayPay: record.holidayPay ?? "",
      levy: record.levy ?? "",
      pension: record.pension ?? "",
      ni: record.ni ?? "",
      sspStartDay:
        record.sspStartDay != null && record.sspStartDay !== ""
          ? Number(record.sspStartDay)
          : "",
    },
    validationSchema: PriceSchema,
    onSubmit: (values) => {
      if (!record?.id) return;
      onSubmit({ id: record.id, ...values });
    },
  });

  return (
    <Card className="mt-3">
      <CardBody>
        <SectionHeader
          title={title}
          meta={
            record?.updatedAt
              ? `Last updated: ${new Date(record.updatedAt).toLocaleString()}`
              : undefined
          }
        />

        {loading && (
          <div className="d-flex align-items-center gap-2 text-muted mb-3">
            <Spinner size="sm" /> <span>Loading…</span>
          </div>
        )}
        {!!error && (
          <div className="text-danger mb-3">
            {error?.response?.data?.message || "Failed to load data."}
          </div>
        )}

        <Form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
        >
          <Row className="gy-3">
            {PRICE_FIELDS.map((f) => (
              <Col md={6} key={f.name}>
                <Label className="form-label" htmlFor={f.name}>
                  {f.label}
                </Label>
                <Input
                  id={f.name}
                  name={f.name}
                  type="number"
                  step={f.integer ? "1" : "0.01"}
                  min={f.integer ? 1 : undefined}
                  max={f.integer ? 31 : undefined}
                  placeholder={f.integer ? "e.g. 4" : "0.00"}
                  value={formik.values[f.name]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched[f.name] && !!formik.errors[f.name]}
                  disabled={loading || submitting || !record?.id}
                />
                <FormFeedback>{formik.errors[f.name]}</FormFeedback>
              </Col>
            ))}

            <Col md={12} className="d-flex gap-2 justify-content-end">
              <Button
                type="button"
                color="secondary"
                outline
                onClick={onRefresh}
                disabled={loading || submitting}
              >
                Refresh
              </Button>
              <Button
                color="primary"
                type="submit"
                disabled={
                  loading || submitting || !formik.isValid || !record?.id
                }
              >
                {submitting ? "Saving…" : "Save Changes"}
              </Button>
            </Col>
          </Row>
        </Form>
      </CardBody>
    </Card>
  );
}

function buildPatchPayload(values) {
  return {
    ...toStringPrices(values),
    sspStartDay: Number(values.sspStartDay),
  };
}

export default function SettingsPrices() {
  const [activeTab, setActiveTab] = useState("worker");

  const {
    mutate: fetchConfig,
    data: configData,
    isPending,
    isError,
    error,
  } = useGetAdminConfiguration();
  const updateConfig = useUpdateAdminConfiguration();

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const workerRecord = useMemo(
    () => extractConfiguration({ data: configData }),
    [configData]
  );

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Settings" breadcrumbItem="Pricing" />

        <Nav tabs>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "worker" })}
              onClick={() => setActiveTab("worker")}
              role="button"
            >
              Admin configuration
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab} className="pt-3">
          <TabPane tabId="worker">
            <PriceForm
              title="Admin configuration"
              loading={isPending}
              error={isError ? error : null}
              record={workerRecord}
              submitting={updateConfig.isPending}
              onRefresh={() => fetchConfig()}
              onSubmit={(values) => {
                updateConfig.mutate(buildPatchPayload(values), {
                  onSuccess: () => {
                    fetchConfig();
                  },
                });
              }}
            />
          </TabPane>
        </TabContent>
      </div>
    </div>
  );
}
